import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { cleanPhone } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { regKey, agentRegKey } = useParams();
  const [searchParams] = useSearchParams();
  const brokerId = searchParams.get('brokerId');
  const isAgentDirect = !!agentRegKey;
  
  const [validKey, setValidKey] = useState(null); // null = loading, true = valid, false = invalid
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    city: '',
    state: 'NJ',
    role: (brokerId || isAgentDirect) ? 'agent' : 'broker',
    broker_id: brokerId || ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    validateKey();
  }, [regKey, agentRegKey]);

  const validateKey = async () => {
    try {
      if (agentRegKey) {
        await axios.get(`${API}/site-settings/validate-agent-key/${agentRegKey}`);
      } else {
        await axios.get(`${API}/site-settings/validate-key/${regKey}`);
      }
      setValidKey(true);
    } catch {
      setValidKey(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Clean phone number and normalize email before submission
      const cleanedData = {
        ...formData,
        email: formData.email.toLowerCase().trim(),
        phone: cleanPhone(formData.phone),
        registration_key: regKey || agentRegKey
      };

      const response = await axios.post(`${API}/auth/register`, cleanedData);
      login(response.data.token, response.data.agent);
      
      // Note: Agent is automatically linked to broker during registration
      // No need to create separate join request
      if (formData.role === 'agent' && formData.broker_id) {
        toast.success('Account created! You are now linked to your broker.');
      } else {
        toast.success('Account created successfully!');
      }
      
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (validKey === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]"></div>
      </div>
    );
  }

  if (validKey === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <ShieldAlert className="mx-auto mb-4 text-red-500" size={64} />
          <h1 className="text-2xl font-bold text-stone-900 mb-2" style={{fontFamily: '"Playfair Display", serif'}}>Invalid Registration Link</h1>
          <p className="text-stone-600 mb-6">This registration link is invalid or has expired. Please contact the administrator for a valid link.</p>
          <Button onClick={() => navigate('/')} className="bg-[#1A4D2E] text-white rounded-full px-8" data-testid="go-home-btn">
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762117360871-f11fbad00ee1?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.5)'
        }}
      />
      
      {/* Register Card */}
      <div className="glass-card rounded-2xl p-8 md:p-12 w-full max-w-md z-10">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>Create Account</h1>
          <p className="text-stone-600">Start managing your open houses</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          {!brokerId && !isAgentDirect && (
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Account Type</Label>
              <select
                data-testid="register-role-select"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="h-14 w-full bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
                required
              >
                <option value="broker">Broker</option>
              </select>
              <p className="text-xs text-stone-500 mt-2">
                Agents can only register using a broker's invitation link
              </p>
            </div>
          )}
          
          {brokerId && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="broker-invite-banner">
              <p className="text-sm text-green-800">
                You're registering as an <strong>Agent</strong> with a broker invitation
              </p>
            </div>
          )}

          {isAgentDirect && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="agent-direct-banner">
              <p className="text-sm text-blue-800">
                You're registering as an <strong>Agent</strong> (independent registration)
              </p>
            </div>
          )}
          
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Full Name</Label>
            <Input
              data-testid="register-name-input"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
              required
            />
          </div>
          
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Email</Label>
            <Input
              data-testid="register-email-input"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
              required
            />
          </div>
          
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Phone (Optional)</Label>
            <PhoneInput
              data-testid="register-phone-input"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">City</Label>
              <Input
                data-testid="register-city-input"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g. Princeton"
                className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">State</Label>
              <select
                data-testid="register-state-input"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="h-14 w-full bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
              >
                {['NJ','NY','PA','CT','DE','FL','TX','CA','GA','MA','MD','NC','OH','VA','IL','AZ','CO','MI','MN','MO','NV','OR','SC','TN','WA','WI'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Password</Label>
            <div className="relative">
              <Input
                data-testid="register-password-input"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          <Button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#1A4D2E] text-white rounded-full h-14 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-green-900/20"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>
        
        <p className="text-center mt-6 text-stone-600">
          Already have an account?{' '}
          <Link to="/login" className="text-[#1A4D2E] font-semibold hover:underline" data-testid="login-link">
            Sign In
          </Link>
        </p>

        <p className="text-center mt-4">
          <Link to="/" className="text-stone-500 hover:text-[#1A4D2E] text-sm hover:underline" data-testid="home-link">
            ← Back to Home
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
